/**
 * The Gravitational Field.
 *
 * AURA Hard Design Brief — Grammar 1: Gravitational Field.
 * Meaning has mass. Certain concepts act as gravity wells.
 * Others orbit, fragment, or escape.
 * Central masses deform nearby space.
 * Distance is effort, not similarity.
 *
 * If everything floats, nothing matters.
 * Density, compression, weight, saturation — these are hierarchy.
 * Color is structural, not decorative.
 *
 * The field must be legible with zero text.
 */

import * as THREE from 'three';
import { useAuraStore } from '../state/store';

const PARTICLE_COUNT = 250_000;

// ---- VERTEX SHADER ----
const fieldVertexShader = /* glsl */ `
uniform float u_time;
uniform float u_entryProgress;
uniform float u_queryHighlight;  // 0 = no query active, 1 = full highlight mode
uniform float u_mode;            // 0=sense, 1=validate, 2=create (continuous for smooth transitions)
uniform float u_modeTransition;  // 0→1 eased transition progress

attribute float particleIntensity;
attribute vec3 particleColor;
attribute float particlePhase;
attribute float particleMass;   // how close to a gravity well (0 = far, 1 = deep in well)
attribute float queryRelevance;  // 0 = irrelevant, 1 = highly relevant (set by query system)
attribute vec3 clusterCenter;   // position of this particle's home cluster center
attribute float clusterWeight;  // weight/intensity of home cluster (for proportional sizing)

varying float vIntensity;
varying vec3 vColor;
varying float vDepth;
varying float vMass;
varying float vRelevance;
varying float vMode;

// Simplex noise for organic motion
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x * 34.0) + 1.0) * x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

float snoise(vec3 v) {
  const vec2 C = vec2(1.0/6.0, 1.0/3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
  vec3 i = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);
  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);
  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;
  i = mod289(i);
  vec4 p = permute(permute(permute(
    i.z + vec4(0.0, i1.z, i2.z, 1.0))
    + i.y + vec4(0.0, i1.y, i2.y, 1.0))
    + i.x + vec4(0.0, i1.x, i2.x, 1.0));
  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;
  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);
  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);
  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);
  vec4 s0 = floor(b0) * 2.0 + 1.0;
  vec4 s1 = floor(b1) * 2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));
  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);
  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
  p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
  vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
}

void main() {
  // ─── MODE-DEPENDENT MOTION PARAMETERS ───────────────────
  // Sense (0):    default — steady, observational
  // Validate (1): dampened — field holds still for examination
  // Create (2):   energized — field is alive, expanding, generative

  // Drift amplitude: how far particles wander
  //   sense=1.0, validate=0.3 (nearly frozen), create=1.6 (expansive)
  float modeDriftMult = 1.0;
  if (u_mode < 0.5) {
    modeDriftMult = 1.0;                                    // sense
  } else if (u_mode < 1.5) {
    modeDriftMult = mix(1.0, 0.0, u_modeTransition);        // validate: FROZEN
  } else {
    modeDriftMult = mix(1.0, 1.6, u_modeTransition);        // create: energize
  }

  // Time scale: how fast particles move
  //   sense=1.0, validate=0.5 (slowed), create=1.4 (faster)
  float modeTimeMult = 1.0;
  if (u_mode < 0.5) {
    modeTimeMult = 1.0;
  } else if (u_mode < 1.5) {
    modeTimeMult = mix(1.0, 0.0, u_modeTransition);        // validate: FROZEN
  } else {
    modeTimeMult = mix(1.0, 1.4, u_modeTransition);
  }

  // Breathing amplitude multiplier
  //   sense=1.0, validate=0.3 (subtle), create=2.0 (dramatic)
  float modeBreatheMult = 1.0;
  if (u_mode < 0.5) {
    modeBreatheMult = 1.0;
  } else if (u_mode < 1.5) {
    modeBreatheMult = mix(1.0, 0.0, u_modeTransition);      // validate: FROZEN
  } else {
    modeBreatheMult = mix(1.0, 2.0, u_modeTransition);
  }

  // Size multiplier — NOT flat. Mass-dependent.
  // Validate: weak signals physically shrink. Dominant structures hold or grow.
  //   "Negative space becomes clearer" = low-mass particles approach invisibility.
  // Create: everything loosens and expands. Mass becomes elastic.
  float modeSizeMult = 1.0;
  if (u_mode < 0.5) {
    modeSizeMult = 1.0;                                      // sense: neutral
  } else if (u_mode < 1.5) {
    // validate: more uniform sizing — the cluster IS the visual unit, not individual particles
    float validateSize = mix(0.6, 1.05, particleMass);
    modeSizeMult = mix(1.0, validateSize, u_modeTransition);
  } else {
    // create: everything expands. low-mass grows more (comes alive)
    float createSize = mix(1.2, 1.15, particleMass);
    modeSizeMult = mix(1.0, createSize, u_modeTransition);
  }

  // ─── MOTION AS MEANING ──────────────────────────────────
  float driftScale = mix(6.0, 1.5, particleMass) * modeDriftMult;
  float timeScale = mix(0.10, 0.04, particleMass) * modeTimeMult;

  float t = u_time * timeScale;
  vec3 noiseInput = position * 0.003 + t + particlePhase;
  vec3 drift = vec3(
    snoise(noiseInput),
    snoise(noiseInput + vec3(100.0)),
    snoise(noiseInput + vec3(200.0))
  ) * driftScale;

  // Breathing: subtle radial pulse (stronger in gravity wells)
  float breatheAmp = mix(0.003, 0.012, particleMass) * modeBreatheMult;
  float breathe = 1.0 + sin(u_time * 0.25 + particlePhase * 6.28) * breatheAmp;

  vec3 pos = position * breathe + drift;

  // ─── VALIDATE COLLAPSE ──────────────────────────────────
  // In validate mode, particles collapse toward their cluster centers
  // forming proportional circles. The field becomes a composed diagram.
  // "AURA earns trust by showing it can hold still and explain itself."
  if (u_mode > 0.5 && u_mode < 1.5) {
    vec3 toCluster = pos - clusterCenter;
    float dist = length(toCluster);
    vec3 dir = dist > 0.001 ? toCluster / dist : vec3(0.0, 1.0, 0.0);

    // Target radius proportional to cluster weight
    // Strong clusters → big circles, weak → small
    float targetRadius = 8.0 + clusterWeight * 40.0;

    // Compress: pull everything to 25% of original distance, capped at proportional radius
    float collapseDist = min(dist * 0.25, targetRadius);
    vec3 collapsePos = clusterCenter + dir * collapseDist;

    pos = mix(pos, collapsePos, u_modeTransition);
  }

  // Entry animation: particles materialize from center outward
  float distFromCenter = length(pos);
  float entryRadius = u_entryProgress * 1200.0;
  float entryFade = smoothstep(entryRadius, entryRadius - 300.0, distFromCenter);
  entryFade *= u_entryProgress;

  vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);

  // SIZE ENCODES HIERARCHY
  float sizeBase = mix(1.8, 5.0, particleMass) + particleIntensity * 4.0;
  float perspective = 700.0 / (-mvPosition.z);
  gl_PointSize = clamp(sizeBase * modeSizeMult * perspective * u_entryProgress, 0.5, 40.0);

  // QUERY HIGHLIGHTING
  float queryDim = mix(1.0, mix(0.15, 1.3, queryRelevance), u_queryHighlight);
  gl_PointSize *= queryDim;

  vIntensity = particleIntensity * entryFade;
  vColor = particleColor;
  vDepth = -mvPosition.z;
  vMass = particleMass;
  vRelevance = queryRelevance;
  vMode = u_mode;

  gl_Position = projectionMatrix * mvPosition;
}
`;

// ---- FRAGMENT SHADER ----
const fieldFragmentShader = /* glsl */ `
uniform float u_queryHighlight;
uniform float u_mode;
uniform float u_modeTransition;

varying float vIntensity;
varying vec3 vColor;
varying float vDepth;
varying float vMass;
varying float vRelevance;
varying float vMode;

void main() {
  vec2 center = gl_PointCoord - vec2(0.5);
  float dist = length(center);
  if (dist > 0.5) discard;

  // ─── MODE-DEPENDENT GLOW ─────────────────────────────────
  // Sense:    default soft glow (6–10 range based on mass)
  // Validate: tighter, crisper edges (analytical precision)
  // Create:   wider, more diffuse glow (generative warmth)
  float gaussBase = mix(10.0, 6.0, vMass);
  float modeGaussShift = 0.0;
  if (vMode > 0.5 && vMode < 1.5) {
    modeGaussShift = 4.0 * u_modeTransition;   // validate: sharper
  } else if (vMode > 1.5) {
    modeGaussShift = -2.5 * u_modeTransition;  // create: softer
  }
  float gaussWidth = gaussBase + modeGaussShift;
  float softEdge = exp(-dist * dist * gaussWidth);

  // Gentle depth fade
  float depthFade = 1.0 / (1.0 + vDepth * 0.00015);

  // Alpha from intensity
  float alpha = softEdge * vIntensity * 3.2 * depthFade;
  if (alpha < 0.001) discard;

  vec3 col = vColor;

  // SATURATION ENCODES HIERARCHY
  float lum = dot(col, vec3(0.299, 0.587, 0.114));
  float satBoost = mix(0.7, 1.2, vMass);

  // ─── MODE-DEPENDENT PHYSICS (behavior first, color second) ──

  if (vMode > 0.5 && vMode < 1.5) {
    // ── VALIDATE ──────────────────────────────────────────
    // "You are examining a claim."
    // Weak signals recede into void. Dominant structures assert with clarity.
    // The field becomes a measurement instrument.

    // BEHAVIOR: aggressive intensity gating — only strong signals survive the diagram.
    // Ambient particles vanish. Filaments recede. Only structure remains.
    float intensityGate = smoothstep(0.06, 0.28, vIntensity);
    float validateFade = mix(0.0, 1.5, intensityGate);
    alpha *= mix(1.0, validateFade, u_modeTransition);

    // BEHAVIOR: mass clarifies contrast. High-mass burns brighter, low-mass recedes.
    satBoost = mix(
      mix(0.7, 1.2, vMass),             // sense values
      mix(0.3, 1.3, vMass),             // validate: crush low, clarify high
      u_modeTransition
    );

    // COLOR (secondary): cool blue-white shift — clinical, analytical
    float coolShift = u_modeTransition * 0.15;
    col.r -= coolShift * 0.3;
    col.b += coolShift * 0.6;

  } else if (vMode > 1.5) {
    // ── CREATE ────────────────────────────────────────────
    // "You are shaping possibility."
    // Everything gains vitality. Even dim signals carry potential.
    // The field radiates generative energy.

    // BEHAVIOR: dim particles gain life. The void is not empty — it hums.
    float createVitality = mix(1.25, 1.05, vMass); // low-mass gets bigger boost
    alpha *= mix(1.0, createVitality, u_modeTransition);

    // BEHAVIOR: saturation rises everywhere — over-saturation is intentional
    satBoost *= mix(1.0, 1.3, u_modeTransition);

    // COLOR (secondary): amber-gold warmth — possibility, not conclusion
    float warmShift = u_modeTransition * 0.10;
    col.r += warmShift;
    col.g += warmShift * 0.35;
    col.b -= warmShift * 0.25;
  }

  col = mix(vec3(lum), col, satBoost);
  col = max(col, vec3(0.0));

  // QUERY HIGHLIGHTING
  float queryDim = mix(1.0, mix(0.08, 1.4, vRelevance), u_queryHighlight);
  alpha *= queryDim;
  float querySat = mix(1.0, mix(0.1, 1.3, vRelevance), u_queryHighlight);
  col = mix(vec3(lum), col, querySat);

  // Cap alpha
  alpha = min(alpha, 0.55);

  gl_FragColor = vec4(col, alpha);
}
`;

export interface NodeBuffers {
  positions: Float32Array;
  scales: Float32Array;
  colors: Float32Array;
  intensities: Float32Array;
  flags: Float32Array;
  count: number;
}

interface ClusterCenter {
  position: [number, number, number];
  color: [number, number, number];
  intensity: number;
  scale: number;
}

export class NodeRenderer {
  public points: THREE.Points;
  public material: THREE.ShaderMaterial;

  // Keep reference to per-particle intensity for hover/selection
  private intensityAttr: THREE.BufferAttribute;
  private baseIntensities: Float32Array;

  // Store cluster info for interaction
  public clusterCenters: ClusterCenter[] = [];

  constructor(buffers: NodeBuffers) {
    // Extract cluster centers from the original node data
    this.clusterCenters = [];
    for (let i = 0; i < buffers.count; i++) {
      this.clusterCenters.push({
        position: [
          buffers.positions[i * 3],
          buffers.positions[i * 3 + 1],
          buffers.positions[i * 3 + 2],
        ],
        color: [
          buffers.colors[i * 3],
          buffers.colors[i * 3 + 1],
          buffers.colors[i * 3 + 2],
        ],
        intensity: buffers.intensities[i],
        scale: buffers.scales[i],
      });
    }

    // Generate the gravitational field
    const { positions, colors, intensities, phases, masses, clusterCenterPositions, clusterWeightValues } = this.generateField();

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('particleColor', new THREE.BufferAttribute(colors, 3));
    this.intensityAttr = new THREE.BufferAttribute(intensities, 1);
    geometry.setAttribute('particleIntensity', this.intensityAttr);
    geometry.setAttribute('particlePhase', new THREE.BufferAttribute(phases, 1));
    geometry.setAttribute('particleMass', new THREE.BufferAttribute(masses, 1));
    geometry.setAttribute('clusterCenter', new THREE.BufferAttribute(clusterCenterPositions, 3));
    geometry.setAttribute('clusterWeight', new THREE.BufferAttribute(clusterWeightValues, 1));

    this.baseIntensities = new Float32Array(intensities);

    // Query relevance attribute (all 0s by default = no query active, shader uses u_queryHighlight to activate)
    const queryRelevanceArr = new Float32Array(PARTICLE_COUNT);
    const queryRelevanceAttr = new THREE.BufferAttribute(queryRelevanceArr, 1);
    queryRelevanceAttr.setUsage(THREE.DynamicDrawUsage);
    geometry.setAttribute('queryRelevance', queryRelevanceAttr);

    this.material = new THREE.ShaderMaterial({
      vertexShader: fieldVertexShader,
      fragmentShader: fieldFragmentShader,
      uniforms: {
        u_time: { value: 0 },
        u_entryProgress: { value: 0 },
        u_queryHighlight: { value: 0 },
        u_mode: { value: 0 },
        u_modeTransition: { value: 1 },
      },
      transparent: true,
      depthWrite: false,
      // NORMAL alpha blending — colors stay true, no additive wash-to-white
      blending: THREE.CustomBlending,
      blendSrc: THREE.SrcAlphaFactor,
      blendDst: THREE.OneMinusSrcAlphaFactor,
      blendEquation: THREE.AddEquation,
    });

    this.points = new THREE.Points(geometry, this.material);
    this.points.frustumCulled = false;

    console.log(`Gravitational field: ${PARTICLE_COUNT.toLocaleString()} particles`);
  }

  /**
   * COMPOSED GRAVITATIONAL FIELD
   *
   * Not a simulation. An argument.
   *
   * Particle allocation is proportional to editorial mass.
   * RTD (mass 1.0) gets ~3× the particles of Distribution (mass 0.35).
   * This creates visible density hierarchy: you SEE what matters.
   *
   * Compression is authored per-character:
   *   dominant  → extremely tight cores, high intensity
   *   entrenched → dense but spread wide, resistant
   *   emerging  → tight forming clusters with trailing wisps
   *   diffuse   → many small pockets, no single mass
   *   fragile   → fractured pockets with intentional gaps
   *   peripheral → small formations orbiting edges
   *   structural → thin threads connecting everything
   *
   * The field must be legible at rest with zero text.
   */
  private generateField() {
    const positions = new Float32Array(PARTICLE_COUNT * 3);
    const colors = new Float32Array(PARTICLE_COUNT * 3);
    const intensities = new Float32Array(PARTICLE_COUNT);
    const phases = new Float32Array(PARTICLE_COUNT);
    const masses = new Float32Array(PARTICLE_COUNT);
    // Validate collapse: each particle remembers its home cluster
    const clusterCenterPositions = new Float32Array(PARTICLE_COUNT * 3);
    const clusterWeightValues = new Float32Array(PARTICLE_COUNT);

    let seed = 12345;
    const rand = () => {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      return seed / 0x7fffffff;
    };
    const gaussRand = (mean: number, std: number) => {
      const u1 = rand();
      const u2 = rand();
      return mean + std * Math.sqrt(-2 * Math.log(Math.max(u1, 0.0001))) * Math.cos(2 * Math.PI * u2);
    };

    const clusters = this.clusterCenters;

    // Build a weighted probability distribution for cluster selection.
    // Heavy clusters attract MORE particles. This is the core editorial act.
    const clusterWeights: number[] = [];
    let totalWeight = 0;
    for (const c of clusters) {
      // Weight = cluster intensity × some power to exaggerate differences
      const w = Math.pow(c.intensity, 1.5) * (c.scale + 0.5);
      clusterWeights.push(w);
      totalWeight += w;
    }
    // Normalize to cumulative distribution
    const cdf: number[] = [];
    let cumulative = 0;
    for (const w of clusterWeights) {
      cumulative += w / totalWeight;
      cdf.push(cumulative);
    }
    // Weighted cluster selection
    const pickCluster = (): number => {
      const r = rand();
      for (let j = 0; j < cdf.length; j++) {
        if (r <= cdf[j]) return j;
      }
      return clusters.length - 1;
    };

    let idx = 0;

    // ── 65% GRAVITY WELL particles ──────────────────────────
    const wellCount = Math.floor(PARTICLE_COUNT * 0.65);
    for (let i = 0; i < wellCount; i++) {
      const ci = pickCluster(); // WEIGHTED selection — heavy clusters get more
      const cluster = clusters[ci];

      // Compression determined by cluster intensity (authored weight)
      const compression = cluster.intensity;
      // Tight cores for strong signals, loose haze for weak ones
      const spread = 10 + (1 - compression) * 50;

      const x = gaussRand(cluster.position[0], spread);
      const y = gaussRand(cluster.position[1], spread);
      const z = gaussRand(cluster.position[2], spread);

      const dx = x - cluster.position[0];
      const dy = y - cluster.position[1];
      const dz = z - cluster.position[2];
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      const nd = dist / Math.max(spread, 1);

      // Intensity: sharp falloff from center. Wells have edges.
      let intensity = compression * Math.exp(-nd * nd * 2.5);
      intensity *= 0.5 + rand() * 0.5;
      intensity = Math.max(intensity, 0.02);

      // Mass: how deep in the gravity well
      const mass = Math.max(0.05, Math.min(1, (1 - nd * 0.6) * compression));

      positions[idx * 3] = x;
      positions[idx * 3 + 1] = y;
      positions[idx * 3 + 2] = z;
      colors[idx * 3] = cluster.color[0];
      colors[idx * 3 + 1] = cluster.color[1];
      colors[idx * 3 + 2] = cluster.color[2];
      intensities[idx] = intensity;
      masses[idx] = mass;
      // Home cluster for validate collapse
      clusterCenterPositions[idx * 3] = cluster.position[0];
      clusterCenterPositions[idx * 3 + 1] = cluster.position[1];
      clusterCenterPositions[idx * 3 + 2] = cluster.position[2];
      clusterWeightValues[idx] = cluster.intensity;
      phases[idx] = rand() * Math.PI * 2;
      idx++;
    }

    // ── 20% FILAMENT particles ──────────────────────────────
    // Connective tissue. Only between NEARBY clusters (not all pairs).
    const filamentCount = Math.floor(PARTICLE_COUNT * 0.20);
    for (let i = 0; i < filamentCount; i++) {
      const ci1 = pickCluster();
      const c1 = clusters[ci1];

      // Find a nearby cluster (prefer spatial neighbors, not random)
      let bestDist = Infinity;
      let ci2 = (ci1 + 1) % clusters.length;
      for (let j = 0; j < clusters.length; j++) {
        if (j === ci1) continue;
        const dx = c1.position[0] - clusters[j].position[0];
        const dy = c1.position[1] - clusters[j].position[1];
        const dz = c1.position[2] - clusters[j].position[2];
        const d = dx * dx + dy * dy + dz * dz;
        // Add randomness so we don't always pick the same neighbor
        const jittered = d * (0.5 + rand());
        if (jittered < bestDist) {
          bestDist = jittered;
          ci2 = j;
        }
      }
      const c2 = clusters[ci2];

      const t = rand();
      // Filament scatter proportional to the distance between clusters
      const pairDist = Math.sqrt(bestDist) * 0.06;
      const scatter = Math.min(pairDist, 25);

      const x = c1.position[0] * (1 - t) + c2.position[0] * t + gaussRand(0, scatter);
      const y = c1.position[1] * (1 - t) + c2.position[1] * t + gaussRand(0, scatter);
      const z = c1.position[2] * (1 - t) + c2.position[2] * t + gaussRand(0, scatter);

      const r = c1.color[0] * (1 - t) + c2.color[0] * t;
      const g = c1.color[1] * (1 - t) + c2.color[1] * t;
      const b = c1.color[2] * (1 - t) + c2.color[2] * t;

      positions[idx * 3] = x;
      positions[idx * 3 + 1] = y;
      positions[idx * 3 + 2] = z;
      colors[idx * 3] = r;
      colors[idx * 3 + 1] = g;
      colors[idx * 3 + 2] = b;
      intensities[idx] = 0.05 + rand() * 0.12;
      masses[idx] = 0.1 + rand() * 0.15;
      // Home cluster — collapse toward primary cluster in validate
      clusterCenterPositions[idx * 3] = c1.position[0];
      clusterCenterPositions[idx * 3 + 1] = c1.position[1];
      clusterCenterPositions[idx * 3 + 2] = c1.position[2];
      clusterWeightValues[idx] = c1.intensity;
      phases[idx] = rand() * Math.PI * 2;
      idx++;
    }

    // ── 15% AMBIENT particles ───────────────────────────────
    // The void is not empty. It carries faint traces of influence.
    // Negative space is intentional — the gaps between clusters are real.
    const ambientCount = PARTICLE_COUNT - idx;
    for (let i = 0; i < ambientCount; i++) {
      const radius = 500 * Math.pow(rand(), 0.3);
      const theta = rand() * Math.PI * 2;
      const phi = Math.acos(2 * rand() - 1);

      const x = radius * Math.sin(phi) * Math.cos(theta);
      const y = radius * Math.sin(phi) * Math.sin(theta);
      const z = radius * Math.cos(phi);

      // Find nearest cluster for trace color
      let minDist = Infinity;
      let nearestCluster = clusters[0];
      for (const c of clusters) {
        const dx = x - c.position[0];
        const dy = y - c.position[1];
        const dz = z - c.position[2];
        const d = dx * dx + dy * dy + dz * dz;
        if (d < minDist) {
          minDist = d;
          nearestCluster = c;
        }
      }

      // Very faint — the void remembers but doesn't shout
      const tint = 0.3 + rand() * 0.3;
      const r = nearestCluster.color[0] * tint + (1 - tint) * 0.03;
      const g = nearestCluster.color[1] * tint + (1 - tint) * 0.03;
      const b = nearestCluster.color[2] * tint + (1 - tint) * 0.04;

      positions[idx * 3] = x;
      positions[idx * 3 + 1] = y;
      positions[idx * 3 + 2] = z;
      colors[idx * 3] = r;
      colors[idx * 3 + 1] = g;
      colors[idx * 3 + 2] = b;
      intensities[idx] = 0.015 + rand() * 0.04;
      masses[idx] = 0.0;
      // Home cluster — collapse toward nearest in validate (these fade out anyway)
      clusterCenterPositions[idx * 3] = nearestCluster.position[0];
      clusterCenterPositions[idx * 3 + 1] = nearestCluster.position[1];
      clusterCenterPositions[idx * 3 + 2] = nearestCluster.position[2];
      clusterWeightValues[idx] = nearestCluster.intensity;
      phases[idx] = rand() * Math.PI * 2;
      idx++;
    }

    // Debug
    console.log('🎨 COMPOSED FIELD — Particle allocation:');
    console.log(`  Wells: ${wellCount}, Filaments: ${filamentCount}, Ambient: ${ambientCount}`);
    for (let j = 0; j < Math.min(6, PARTICLE_COUNT); j++) {
      const cr = colors[j * 3].toFixed(3);
      const cg = colors[j * 3 + 1].toFixed(3);
      const cb = colors[j * 3 + 2].toFixed(3);
      console.log(`  [${j}] rgb(${cr}, ${cg}, ${cb}) int=${intensities[j].toFixed(3)} mass=${masses[j].toFixed(3)}`);
    }

    return { positions, colors, intensities, phases, masses, clusterCenterPositions, clusterWeightValues };
  }

  // --- Compatibility methods (called by existing interaction systems) ---

  get mesh(): THREE.Points { return this.points; }

  setNodeFlag(_index: number, _flag: number) {
    // TODO: highlight region around cluster center
  }

  clearFlag(_flag: number) {
    // TODO: clear highlights
  }

  getNodePosition(index: number): THREE.Vector3 {
    if (index < 0 || index >= this.clusterCenters.length) {
      return new THREE.Vector3();
    }
    const c = this.clusterCenters[index];
    return new THREE.Vector3(...c.position);
  }

  /**
   * Set per-particle query relevance (called by QuerySystem).
   * nodeScores has one entry per SyntheticNode — we need to propagate
   * to all 250K particles based on which cluster they belong to.
   */
  setQueryRelevance(nodeScores: Float32Array) {
    const attr = this.points.geometry.getAttribute('queryRelevance') as THREE.BufferAttribute;
    const data = attr.array as Float32Array;
    const clusters = this.clusterCenters;

    // For each particle, find the nearest cluster and use its score
    const positions = this.points.geometry.getAttribute('position').array as Float32Array;

    for (let i = 0; i < data.length; i++) {
      const px = positions[i * 3];
      const py = positions[i * 3 + 1];
      const pz = positions[i * 3 + 2];

      let bestDist = Infinity;
      let bestScore = 0;

      for (let j = 0; j < clusters.length; j++) {
        const c = clusters[j];
        const dx = px - c.position[0];
        const dy = py - c.position[1];
        const dz = pz - c.position[2];
        const d = dx * dx + dy * dy + dz * dz;
        if (d < bestDist) {
          bestDist = d;
          bestScore = j < nodeScores.length ? nodeScores[j] : 0;
        }
      }

      data[i] = bestScore;
    }

    attr.needsUpdate = true;
  }

  /** Clear query relevance (all particles return to normal) */
  clearQueryRelevance() {
    const attr = this.points.geometry.getAttribute('queryRelevance') as THREE.BufferAttribute;
    const data = attr.array as Float32Array;
    data.fill(0);
    attr.needsUpdate = true;
  }

  update(time: number, entryProgress: number, mode: number) {
    this.material.uniforms.u_time.value = time;
    this.material.uniforms.u_entryProgress.value = entryProgress;
    this.material.uniforms.u_mode.value = mode;

    // Get transition progress from store
    const { modeTransitionProgress } = useAuraStore.getState();
    this.material.uniforms.u_modeTransition.value = modeTransitionProgress;
  }

  dispose() {
    this.points.geometry.dispose();
    this.material.dispose();
  }
}
